import { useState } from "react";
import { useLocation } from "wouter";
import { useAuthMe, useSignOut, type UserTier } from "@vorloop/api-client-react";
import { NavRail } from "../components/layout/NavRail";
import { WorkspaceSwitcher } from "../components/layout/WorkspaceSwitcher";

/**
 * Read-only profile page. Handle editing + delete-account land later
 * (Wave 2b) — for now the goal is a destination that's a real page,
 * not a 404, with the controls a user actually expects to see.
 */
export function Settings() {
  const [, setLocation] = useLocation();
  const me = useAuthMe();
  const signOut = useSignOut({
    onSuccess: () => setLocation("/signin"),
  });
  const handle = me.data?.user?.handle ?? "you";
  const user = me.data?.user;

  return (
    <div style={{ minHeight: "100vh", display: "flex", background: "var(--bg-canvas)" }}>
      <NavRail handle={handle} />
      <main
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}
      >
        <header
          style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "10px 24px",
          }}
        >
          <WorkspaceSwitcher handle={handle} compact />
        </header>

        <div
          style={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            padding: "32px 32px 80px",
          }}
        >
          <div style={{ width: "100%", maxWidth: 720, display: "flex", flexDirection: "column", gap: 24 }}>
            <header>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.01em" }}>
                Settings
              </h1>
              <p style={{ color: "var(--text-tertiary)", fontSize: 13, marginTop: 6 }}>
                Your account and session.
              </p>
            </header>

            <section style={cardStyle}>
              <div style={sectionTitleStyle}>Profile</div>
              <Field label="Handle" value={user?.handle ?? "—"} />
              <Field label="Email" value={user?.email ?? "—"} />
              <Field label="Tier" value={(user?.tier ?? "free").toUpperCase()} pill />
              <div style={{ color: "var(--text-tertiary)", fontSize: 11, marginTop: 4 }}>
                Renaming and email change land in a follow-up.
              </div>
            </section>

            <BillingSection tier={user?.tier ?? "free"} />

            <KeyboardShortcutsSection />

            <section style={cardStyle}>
              <div style={sectionTitleStyle}>Session</div>
              <button
                type="button"
                onClick={() => signOut.mutate()}
                disabled={signOut.isPending}
                style={{
                  alignSelf: "flex-start",
                  padding: "8px 14px",
                  background: signOut.isPending ? "var(--bg-active)" : "var(--accent-red)",
                  color: signOut.isPending ? "var(--text-tertiary)" : "#fff",
                  border: "none",
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: signOut.isPending ? "not-allowed" : "pointer",
                }}
              >
                {signOut.isPending ? "Signing out…" : "Sign out"}
              </button>
              {signOut.error && (
                <div style={{ color: "var(--accent-red)", fontSize: 12, marginTop: 8 }}>
                  {signOut.error.message}
                </div>
              )}
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}

function Field({ label, value, pill }: { label: string; value: string; pill?: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
      <span style={{ color: "var(--text-tertiary)", fontSize: 12 }}>{label}</span>
      {pill ? (
        <span
          style={{
            padding: "3px 10px",
            background: "rgba(99,102,241,0.12)",
            color: "#a5b4fc",
            border: "1px solid rgba(99,102,241,0.4)",
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 0.4,
          }}
        >
          {value}
        </span>
      ) : (
        <span style={{ color: "var(--text-primary)", fontSize: 13, fontFamily: "var(--font-mono)" }}>
          {value}
        </span>
      )}
    </div>
  );
}

interface PlanRow {
  readonly tier: UserTier;
  readonly label: string;
  readonly price: string;
  readonly headline: string;
  readonly features: ReadonlyArray<string>;
  readonly recommended?: boolean;
}

const PLANS: ReadonlyArray<PlanRow> = [
  {
    tier: "free",
    label: "Free",
    price: "$0",
    headline: "5 builds / month · Kimi K2 free",
    features: ["5 agent turns per month", "Public projects only", "Kimi K2 free model"],
  },
  {
    tier: "plus",
    label: "Plus",
    price: "$20/mo",
    headline: "Unlimited builds · Sonnet 4.5",
    features: [
      "Unlimited agent turns",
      "Private projects",
      "Claude Sonnet 4.5",
      "Image generation (FLUX)",
      "Custom domains",
    ],
    recommended: true,
  },
  {
    tier: "pro",
    label: "Pro",
    price: "$80/mo",
    headline: "Team workspaces · priority queue",
    features: ["Everything in Plus", "Multi-seat workspaces", "Priority compute queue", "Higher rate limits"],
  },
];

function BillingSection({ tier }: { tier: UserTier }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const current = PLANS.find((p) => p.tier === tier);

  function startCheckout(target: PlanRow) {
    // Stripe Checkout session creation lives in step 3 (real-API
    // hookup). For now we surface a clear placeholder so the rest of
    // the flow can be e2e-tested without a Stripe account.
    alert(
      `Mock checkout for ${target.label} ($${target.price}).\n\n` +
        "Stripe wiring lands once we set STRIPE_SECRET_KEY + price IDs " +
        "in the live API server.",
    );
    setPickerOpen(false);
  }

  return (
    <section style={cardStyle}>
      <div style={sectionTitleStyle}>Plan & billing</div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 500 }}>{current?.label ?? "Free"} plan</div>
          <div style={{ color: "var(--text-tertiary)", fontSize: 12, marginTop: 2 }}>
            {current?.headline ?? "5 builds / month"}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setPickerOpen((v) => !v)}
          style={{
            padding: "8px 14px",
            background: pickerOpen ? "var(--bg-active)" : "var(--accent-blue)",
            color: pickerOpen ? "var(--text-secondary)" : "#fff",
            border: "none",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          {pickerOpen ? "Close" : tier === "free" ? "Upgrade" : "Change plan"}
        </button>
      </div>
      {pickerOpen && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 10,
            marginTop: 4,
          }}
        >
          {PLANS.map((p) => {
            const isCurrent = p.tier === tier;
            return (
              <div
                key={p.tier}
                style={{
                  border: p.recommended ? "1px solid var(--accent-blue)" : "1px solid var(--border-default)",
                  borderRadius: 10,
                  padding: 12,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  background: "var(--bg-panel-2)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{p.label}</span>
                  {p.recommended && (
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 700,
                        letterSpacing: 0.6,
                        textTransform: "uppercase",
                        background: "rgba(99,102,241,0.18)",
                        color: "#a5b4fc",
                        padding: "1px 6px",
                        borderRadius: 999,
                      }}
                    >
                      Recommended
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 18, fontWeight: 600 }}>{p.price}</div>
                <ul style={{ margin: 0, padding: 0, listStyle: "none", color: "var(--text-secondary)", fontSize: 12 }}>
                  {p.features.map((f) => (
                    <li key={f} style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4 }}>
                      <span style={{ color: "#34d399" }}>✓</span>
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  disabled={isCurrent}
                  onClick={() => startCheckout(p)}
                  style={{
                    marginTop: 4,
                    padding: "6px 10px",
                    background: isCurrent ? "var(--bg-active)" : "var(--accent-blue)",
                    color: isCurrent ? "var(--text-tertiary)" : "#fff",
                    border: "none",
                    borderRadius: 7,
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: isCurrent ? "default" : "pointer",
                  }}
                >
                  {isCurrent ? "Current plan" : `Switch to ${p.label}`}
                </button>
              </div>
            );
          })}
        </div>
      )}
      <div style={{ color: "var(--text-tertiary)", fontSize: 11 }}>
        Stripe checkout lands when the live API server is configured with publishable + price IDs.
      </div>
    </section>
  );
}

interface ShortcutRow {
  readonly id: string;
  readonly command: string;
  readonly description: string;
  readonly binding: string;
}

const DEFAULT_SHORTCUTS: ReadonlyArray<ShortcutRow> = [
  { id: "comment.add", command: "Add line comment", description: "Comment the current selection using line comments", binding: "" },
  { id: "comment.remove", command: "Remove line comment", description: "Uncomment the current selection using line comments", binding: "" },
  { id: "comment.toggle", command: "Toggle comment", description: "Comment or uncomment the current selection", binding: "⌘ /" },
  { id: "comment.block", command: "Toggle block comment", description: "Comment or uncomment using block comments", binding: "⌘ ⇧ A" },
  { id: "accept.primary", command: "Accept primary completion", description: "", binding: "Tab" },
  { id: "accept.secondary", command: "Accept secondary completion", description: "", binding: "" },
  { id: "trigger.completion", command: "Trigger completion", description: "", binding: "⌃ Space" },
  { id: "cancel.completion", command: "Cancel completion", description: "", binding: "Escape" },
  { id: "search.find-next", command: "Search: Find next", description: "", binding: "⌘ G" },
  { id: "search.find-prev", command: "Search: Find previous", description: "", binding: "⌘ ⇧ G" },
  { id: "format.doc", command: "Format Document", description: "", binding: "⌘ ⇧ S" },
  { id: "go.definition", command: "Go to Definition", description: "", binding: "F12" },
  { id: "go.type-definition", command: "Go to Type Definition", description: "", binding: "" },
  { id: "rename.symbol", command: "Rename Symbol", description: "", binding: "F2" },
  { id: "git.commit", command: "Git: Commit", description: "", binding: "⌃ ↵" },
  { id: "console.clear", command: "Clear console history", description: "", binding: "" },
];

function KeyboardShortcutsSection() {
  const storageKey = "vp.shortcuts";
  const [rows, setRows] = useState<ShortcutRow[]>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) return JSON.parse(raw) as ShortcutRow[];
    } catch {
      // ignore
    }
    return DEFAULT_SHORTCUTS.map((r) => ({ ...r }));
  });
  const [query, setQuery] = useState("");

  function update(id: string, binding: string) {
    const next = rows.map((r) => (r.id === id ? { ...r, binding } : r));
    setRows(next);
    try {
      localStorage.setItem(storageKey, JSON.stringify(next));
    } catch {
      // ignore
    }
  }

  function reset() {
    setRows(DEFAULT_SHORTCUTS.map((r) => ({ ...r })));
    try {
      localStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
  }

  const q = query.trim().toLowerCase();
  const filtered = q ? rows.filter((r) => r.command.toLowerCase().includes(q) || r.binding.toLowerCase().includes(q)) : rows;

  return (
    <section style={cardStyle}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <div style={sectionTitleStyle}>Keyboard shortcuts</div>
        <button
          type="button"
          onClick={reset}
          style={{
            padding: "4px 10px",
            background: "transparent",
            border: "1px solid var(--border-default)",
            color: "var(--text-secondary)",
            borderRadius: 6,
            fontSize: 11,
            cursor: "pointer",
          }}
        >
          Reset to defaults
        </button>
      </div>
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Filter by command or binding…"
        style={{
          padding: "8px 10px",
          background: "var(--bg-canvas)",
          border: "1px solid var(--border-default)",
          borderRadius: 7,
          color: "var(--text-primary)",
          fontSize: 13,
          outline: "none",
        }}
      />
      <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 4 }}>
        {filtered.map((r) => (
          <li
            key={r.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "8px 12px",
              background: "var(--bg-panel-2)",
              borderRadius: 6,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, color: "var(--text-primary)" }}>{r.command}</div>
              {r.description && (
                <div style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 2 }}>{r.description}</div>
              )}
            </div>
            <input
              type="text"
              value={r.binding}
              onChange={(e) => update(r.id, e.target.value)}
              placeholder="+ Add shortcut"
              style={{
                width: 130,
                padding: "5px 8px",
                background: "var(--bg-canvas)",
                border: "1px solid var(--border-default)",
                borderRadius: 6,
                color: "var(--text-primary)",
                fontSize: 12,
                fontFamily: "var(--font-mono)",
                outline: "none",
                textAlign: "center",
              }}
            />
          </li>
        ))}
      </ol>
      <div style={{ color: "var(--text-tertiary)", fontSize: 11 }}>
        Stored client-side. Wires to a per-user prefs vault when the live API ships.
      </div>
    </section>
  );
}

const cardStyle: React.CSSProperties = {
  border: "1px solid var(--border-default)",
  background: "var(--bg-panel)",
  borderRadius: 14,
  padding: 18,
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "var(--text-tertiary)",
  marginBottom: 4,
};
